import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Play, Pause, RotateCcw, Shield, Target, Radio, Zap } from 'lucide-react';

// 10-threat swarm: 4 air (triangular), 3 surface (cuboidal), 3 underwater (sphere)
const SWARM_TEMPLATES = [
  { name: 'FPV Kamikaze',          domain: 'air',        color: 0xef4444, speed: 0.10, threatLevel: 'critical' },
  { name: 'Recon Quadcopter',      domain: 'air',        color: 0xf59e0b, speed: 0.07, threatLevel: 'medium' },
  { name: 'Loitering Munition',    domain: 'air',        color: 0xef4444, speed: 0.09, threatLevel: 'high' },
  { name: 'Surveillance Fixed-Wing', domain: 'air',      color: 0xf59e0b, speed: 0.08, threatLevel: 'low' },
  { name: 'Surface USV Alpha',     domain: 'surface',    color: 0x22c55e, speed: 0.05, threatLevel: 'high' },
  { name: 'Surface USV Bravo',     domain: 'surface',    color: 0x22c55e, speed: 0.055, threatLevel: 'medium' },
  { name: 'Surface USV Charlie',   domain: 'surface',    color: 0x16a34a, speed: 0.045, threatLevel: 'high' },
  { name: 'Subsurface UUV-1',      domain: 'underwater', color: 0x3b82f6, speed: 0.035, threatLevel: 'medium' },
  { name: 'Subsurface UUV-2',      domain: 'underwater', color: 0x2563eb, speed: 0.04, threatLevel: 'high' },
  { name: 'Subsurface UUV-3',      domain: 'underwater', color: 0x60a5fa, speed: 0.03, threatLevel: 'medium' },
];

// Each soft-kill produces a distinct visual reaction encoded as `behavior`:
//   'spiral_down' — swirling descent (Cyber Takeover)
//   'tumble_down' — erratic loss-of-control fall (Jamming / Comm Disruption)
//   'wander_away' — drifts away from ship in a random heading (Spoofing)
//   'acoustic_pushback' — underwater acoustic waves shoo the drone outward
const SOFT_KILL_METHODS = {
  air: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'cyber_takeover', label: 'Cyber Takeover', color: '#a855f7', behavior: 'spiral_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  surface: [
    { id: 'rf_jamming', label: 'RF Jamming', color: '#eab308', behavior: 'tumble_down' },
    { id: 'gps_spoofing', label: 'GPS Spoofing', color: '#3b82f6', behavior: 'wander_away' },
  ],
  underwater: [
    { id: 'acoustic_blaster', label: 'Acoustic Blaster', color: '#06b6d4', behavior: 'acoustic_pushback' },
  ],
};

export default function SimulationScenario3() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const animationRef = useRef(null);

  const playingRef = useRef(false);
  const threatsRef = useRef([]);
  const spawnedRef = useRef(false);
  const statsRef = useRef({ detected: 0, soft_killed: 0, neutralized: 0 });
  const logsRef = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeThreats, setActiveThreats] = useState([]);
  const [stats, setStats] = useState({ detected: 0, soft_killed: 0, neutralized: 0 });

  const pushLog = useCallback((message, type = 'info') => {
    const entry = { ts: new Date().toLocaleTimeString(), message, type };
    logsRef.current = [...logsRef.current.slice(-40), entry];
    setLogs([...logsRef.current]);
  }, []);

  const bumpStat = useCallback((field) => {
    statsRef.current = { ...statsRef.current, [field]: statsRef.current[field] + 1 };
    setStats({ ...statsRef.current });
  }, []);

  const syncActiveThreats = useCallback(() => {
    const list = threatsRef.current
      .filter(t => t.visible && t.userData.status !== 'neutralized')
      .map(t => ({
        id: t.userData.id,
        name: t.userData.name,
        domain: t.userData.domain,
        status: t.userData.status,
        threatLevel: t.userData.threatLevel,
        distance: t.position.distanceTo(new THREE.Vector3(0, 0, 0)).toFixed(1),
        soft_kill_label: t.userData.soft_kill_label,
      }));
    setActiveThreats(list);
  }, []);

  const cbRef = useRef({ pushLog, bumpStat, syncActiveThreats });
  useEffect(() => {
    cbRef.current = { pushLog, bumpStat, syncActiveThreats };
  }, [pushLog, bumpStat, syncActiveThreats]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    playingRef.current = false;
    threatsRef.current = [];
    spawnedRef.current = false;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);
    scene.fog = new THREE.FogExp2(0x020617, 0.005);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.set(45, 35, 45);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 15;
    controls.maxDistance = 130;

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    // Ocean (translucent so we can see the underwater hemisphere)
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(300, 300),
      new THREE.MeshStandardMaterial({ color: 0x0c4a6e, transparent: true, opacity: 0.32, side: THREE.DoubleSide })
    );
    ocean.rotation.x = -Math.PI / 2;
    ocean.position.y = -1;
    scene.add(ocean);

    const grid = new THREE.GridHelper(220, 44, 0x1e3a5f, 0x0f2847);
    grid.position.y = -0.9;
    scene.add(grid);

    // Ship (yellow flagship)
    const shipGroup = new THREE.Group();
    const s = 0.55;
    const hull = new THREE.Mesh(new THREE.BoxGeometry(2*s, 1*s, 6*s), new THREE.MeshStandardMaterial({ color: 0xfbbf24, metalness: 0.5, roughness: 0.3 }));
    hull.position.set(0, -0.3*s, 0);
    shipGroup.add(hull);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(1.8*s, 0.2*s, 5.5*s), new THREE.MeshStandardMaterial({ color: 0xf59e0b }));
    deck.position.set(0, 0.2*s, 0);
    shipGroup.add(deck);
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(1.2*s, 1*s, 1.5*s), new THREE.MeshStandardMaterial({ color: 0xeab308 }));
    bridge.position.set(0, 0.8*s, -0.8*s);
    shipGroup.add(bridge);
    const dome = new THREE.Mesh(new THREE.SphereGeometry(0.22*s, 16, 16), new THREE.MeshStandardMaterial({ color: 0x0ea5e9, emissive: 0x0ea5e9, emissiveIntensity: 0.6 }));
    dome.position.set(0, 2.1*s, -0.8*s);
    shipGroup.add(dome);
    scene.add(shipGroup);

    // Defense zones (5km / 2km)
    const addZone = (radius, color, opacity) => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 32, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, wireframe: true, side: THREE.DoubleSide })
      );
      sphere.userData.isZone = true;
      scene.add(sphere);
    };
    addZone(15, 0x0ea5e9, 0.06);
    addZone(6, 0xeab308, 0.09);

    [15, 6].forEach(r => {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(r - 0.05, r + 0.05, 64),
        new THREE.MeshBasicMaterial({ color: 0x1e3a5f, side: THREE.DoubleSide })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -0.8;
      scene.add(ring);
    });

    // Build threat mesh by domain shape:
    // air = triangular (TetrahedronGeometry — pyramid-like)
    // surface = cuboidal (BoxGeometry)
    // underwater = sphere
    function createThreatMesh(domain, color) {
      let geo;
      if (domain === 'air') geo = new THREE.TetrahedronGeometry(0.85, 0);
      else if (domain === 'surface') geo = new THREE.BoxGeometry(1.1, 0.55, 0.85);
      else geo = new THREE.SphereGeometry(0.6, 24, 24);

      const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 0.65, roughness: 0.4
      }));

      // Domain marker ring
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.85, 0.95, 32),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
      );
      ring.rotation.x = Math.PI / 2;
      mesh.add(ring);
      return mesh;
    }

    function spawnSwarm() {
      if (spawnedRef.current) return;
      spawnedRef.current = true;

      const yMap = { air: () => 6 + Math.random() * 6, surface: () => 0.4, underwater: () => -3.5 - Math.random() * 2.5 };
      // Distribute angles across the full circle so threats come from different bearings
      SWARM_TEMPLATES.forEach((tpl, idx) => {
        const angle = (idx / SWARM_TEMPLATES.length) * Math.PI * 2 + (Math.random() * 0.25);
        const dist = 22 + Math.random() * 6;
        const startPos = new THREE.Vector3(Math.cos(angle) * dist, yMap[tpl.domain](), Math.sin(angle) * dist);

        const id = `T-${String(idx + 1).padStart(2, '0')}`;
        const mesh = createThreatMesh(tpl.domain, tpl.color);
        mesh.position.copy(startPos);
        mesh.userData = {
          id, name: tpl.name, domain: tpl.domain, color: tpl.color,
          speed: tpl.speed * (0.85 + Math.random() * 0.3),
          threatLevel: tpl.threatLevel,
          status: 'approaching', detected: false, tracking: false,
          softKillApplied: false, soft_kill_label: null,
          spinAxis: new THREE.Vector3(Math.random(), Math.random(), Math.random()).normalize(),
        };
        scene.add(mesh);
        threatsRef.current.push(mesh);
      });

      cbRef.current.pushLog(`[ALERT] SWARM INBOUND — ${SWARM_TEMPLATES.length} threats detected across all domains`, 'danger');
      cbRef.current.syncActiveThreats();
    }

    function updateThreats() {
      const ship = new THREE.Vector3(0, 0, 0);
      let stateChanged = false;

      threatsRef.current.forEach(threat => {
        if (!threat.visible) return;
        const ud = threat.userData;
        if (ud.status === 'neutralized') return;

        // Soft-killed: branch on the kill method's distinct behavior
        if (ud.status === 'soft_killed') {
          const beh = ud.kill_behavior;
          const colorHex = ud.kill_color || 0xffffff;
          ud.killTick = (ud.killTick || 0) + 1;

          if (beh === 'spiral_down') {
            // Cyber Takeover — swirling descent (drone hijacked & forced down)
            const t = ud.killTick;
            const radius = 0.04 * t;
            const center = ud.killOrigin;
            threat.position.x = center.x + Math.cos(t * 0.18) * radius;
            threat.position.z = center.z + Math.sin(t * 0.18) * radius;
            threat.position.y -= 0.06;
            threat.rotation.y += 0.18;
            threat.rotation.x += 0.04;
            // Trailing swirl particles
            if (t % 4 === 0) {
              const p = new THREE.Mesh(
                new THREE.SphereGeometry(0.12, 8, 8),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.85 })
              );
              p.position.copy(threat.position);
              p.userData.isEffect = true;
              p.userData.life = 0.8;
              scene.add(p);
            }
          } else if (beh === 'tumble_down') {
            // Jamming / Comm Disruption — loss of control, erratic tumble
            threat.position.y -= 0.07;
            threat.rotation.x += 0.13 + Math.sin(ud.killTick * 0.3) * 0.05;
            threat.rotation.z += 0.10 + Math.cos(ud.killTick * 0.4) * 0.05;
            // Random horizontal drift
            threat.position.x += Math.sin(ud.killTick * 0.4) * 0.04;
            threat.position.z += Math.cos(ud.killTick * 0.35) * 0.04;
            // Spark sputter
            if (ud.killTick % 3 === 0) {
              const spark = new THREE.Mesh(
                new THREE.SphereGeometry(0.14, 6, 6),
                new THREE.MeshBasicMaterial({ color: 0xfde047, transparent: true, opacity: 0.95 })
              );
              spark.position.copy(threat.position);
              spark.position.x += (Math.random() - 0.5) * 0.6;
              spark.position.y += (Math.random() - 0.5) * 0.6;
              spark.position.z += (Math.random() - 0.5) * 0.6;
              spark.userData.isEffect = true;
              spark.userData.life = 0.4;
              scene.add(spark);
            }
          } else if (beh === 'wander_away') {
            // GPS Spoofing — drone "thinks" it's somewhere else; wanders away from ship
            if (!ud.wanderDir) {
              const baseAway = threat.position.clone().normalize();
              // Add small lateral perturbation to make it look like a confused track
              const perp = new THREE.Vector3(-baseAway.z, 0, baseAway.x).multiplyScalar(0.5 * (Math.random() - 0.5));
              ud.wanderDir = baseAway.add(perp).normalize();
            }
            threat.position.add(ud.wanderDir.clone().multiplyScalar(0.09));
            // Slight altitude wobble for air drones
            if (ud.domain === 'air') threat.position.y += Math.sin(ud.killTick * 0.1) * 0.05;
            threat.rotation.y += 0.04;
            // Phantom echo trail
            if (ud.killTick % 6 === 0) {
              const echo = new THREE.Mesh(
                new THREE.RingGeometry(0.5, 0.7, 24),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.6, side: THREE.DoubleSide })
              );
              echo.position.copy(threat.position);
              echo.lookAt(0, threat.position.y, 0);
              echo.userData.isEffect = true;
              echo.userData.life = 0.7;
              scene.add(echo);
            }
          } else if (beh === 'acoustic_pushback') {
            // Acoustic Blaster — pressure waves push the underwater drone radially outward
            const out = threat.position.clone();
            out.y = threat.position.y; // stay at depth
            const radial = new THREE.Vector3(out.x, 0, out.z).normalize();
            threat.position.add(radial.multiplyScalar(0.10));
            threat.position.y -= 0.025; // sinks slightly
            threat.rotation.x += 0.08;
            threat.rotation.z += 0.08;
            // Periodic expanding water rings emitted from ship toward drone
            if (ud.killTick % 8 === 0) {
              const ringMid = new THREE.Vector3().lerpVectors(new THREE.Vector3(0, threat.position.y, 0), threat.position, 0.5);
              const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.5, 0.7, 32),
                new THREE.MeshBasicMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.75, side: THREE.DoubleSide })
              );
              ring.position.copy(ringMid);
              ring.rotation.x = -Math.PI / 2; // horizontal disc
              ring.userData.isEffect = true;
              ring.userData.life = 1.0;
              ring.userData.expands = true;
              scene.add(ring);
            }
          } else {
            // Fallback (shouldn't hit) — generic descent
            threat.position.y -= 0.06;
            threat.rotation.z += 0.06;
          }

          // Fade out the threat over time
          if (threat.material) {
            threat.material.transparent = true;
            threat.material.opacity = Math.max(0.0, (threat.material.opacity ?? 1) - 0.008);
          }

          // Termination: out-of-bounds or fully faded — also wander_away terminates when far from ship
          const dist = threat.position.distanceTo(new THREE.Vector3(0, 0, 0));
          const finished =
            (threat.material && threat.material.opacity <= 0.05) ||
            threat.position.y < -8 ||
            (beh === 'wander_away' && dist > 40) ||
            (beh === 'acoustic_pushback' && dist > 35);

          if (finished) {
            threat.visible = false;
            ud.status = 'neutralized';
            cbRef.current.bumpStat('neutralized');
            stateChanged = true;
          }
          return;
        }

        // Approach: move towards ship
        const dist = threat.position.distanceTo(ship);
        const dir = new THREE.Vector3().subVectors(ship, threat.position).normalize();
        const targetY = ud.domain === 'air' ? 4 : ud.domain === 'underwater' ? -3 : 0.4;
        dir.y = (targetY - threat.position.y) * 0.02;
        threat.position.add(dir.multiplyScalar(ud.speed));

        // Spin / pulse
        threat.rotation.x += 0.012;
        threat.rotation.y += 0.018;
        const sc = 1 + Math.sin(Date.now() * 0.004) * 0.10;
        threat.scale.setScalar(sc);

        // 5km detect
        if (dist < 16 && !ud.detected) {
          ud.detected = true;
          ud.status = 'detected';
          cbRef.current.pushLog(`[5KM] ${ud.id} ${ud.name} DETECTED — ${ud.threatLevel.toUpperCase()} (${ud.domain})`, 'warning');
          cbRef.current.bumpStat('detected');
          stateChanged = true;
        }

        // 4km tracking
        if (dist < 13 && ud.detected && !ud.tracking) {
          ud.tracking = true;
          ud.status = 'tracking';
          cbRef.current.pushLog(`[TRACK] ${ud.id} sensors locked — Range: ${(dist * 0.333).toFixed(1)}km`, 'info');
          stateChanged = true;
        }

        // 2km auto soft-kill (always succeeds in this scenario)
        if (dist < 7 && !ud.softKillApplied) {
          ud.softKillApplied = true;
          const methods = SOFT_KILL_METHODS[ud.domain];
          const sk = methods[Math.floor(Math.random() * methods.length)];
          ud.soft_kill_label = sk.label;
          ud.kill_behavior = sk.behavior;
          ud.kill_color = parseInt(sk.color.replace('#', '0x'));
          ud.killOrigin = threat.position.clone();
          cbRef.current.pushLog(`[2KM] ${ud.id} AUTO SOFT KILL: ${sk.label} deployed`, 'danger');

          const colorHex = ud.kill_color;

          if (sk.behavior === 'acoustic_pushback') {
            // Acoustic Blaster — concentric water rings expanding from ship at depth
            for (let i = 0; i < 4; i++) {
              const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.5, 0.55 + i * 0.5, 48),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.9, side: THREE.DoubleSide })
              );
              ring.position.set(0, threat.position.y, 0);
              ring.rotation.x = -Math.PI / 2;
              ring.userData.isEffect = true;
              ring.userData.life = 1.0;
              ring.userData.expands = true;
              ring.userData.expandRate = 0.15;
              scene.add(ring);
            }
          } else if (sk.behavior === 'spiral_down') {
            // Cyber Takeover — purple lattice cube briefly engulfs the drone
            const cage = new THREE.Mesh(
              new THREE.BoxGeometry(2, 2, 2),
              new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.5, wireframe: true })
            );
            cage.position.copy(threat.position);
            cage.userData.isEffect = true;
            cage.userData.life = 1.0;
            scene.add(cage);
          } else if (sk.behavior === 'wander_away') {
            // GPS Spoofing — phantom marker offset from drone (the false position)
            const phantom = new THREE.Mesh(
              new THREE.SphereGeometry(0.4, 12, 12),
              new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.6 })
            );
            phantom.position.copy(threat.position);
            phantom.position.x += (Math.random() - 0.5) * 4;
            phantom.position.z += (Math.random() - 0.5) * 4;
            phantom.userData.isEffect = true;
            phantom.userData.life = 1.0;
            scene.add(phantom);
          } else {
            // Tumble (Jamming / Comm Disruption) — radial RF/comm shock waves
            for (let i = 0; i < 4; i++) {
              const wave = new THREE.Mesh(
                new THREE.RingGeometry(0.4 + i * 0.6, 0.55 + i * 0.6, 32),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
              );
              wave.position.copy(threat.position);
              wave.lookAt(ship);
              wave.userData.isEffect = true;
              wave.userData.life = 1.0;
              scene.add(wave);
            }
          }

          // No directed beam — Scenario 3 is soft-kill only. The drone-side
          // shock waves / cage / phantom marker above already indicate the
          // engagement without implying a laser hard-kill.

          // Always succeed in Scenario 3
          setTimeout(() => {
            ud.status = 'soft_killed';
            cbRef.current.pushLog(`[NEUTRALIZED] ${ud.id} soft kill SUCCESS — ${sk.label} effective`, 'success');
            cbRef.current.bumpStat('soft_killed');
            cbRef.current.syncActiveThreats();
          }, 700 + Math.random() * 600);

          stateChanged = true;
        }
      });

      // Effects cleanup
      const toRemove = [];
      scene.children.forEach(c => {
        if (c.userData.isEffect) {
          c.userData.life -= 0.02;
          if (c.userData.expands) {
            const grow = 1 + (1 - c.userData.life) * 4;
            c.scale.setScalar(grow);
          }
          if (c.material) c.material.opacity = Math.max(c.userData.life, 0);
          if (c.userData.life <= 0) toRemove.push(c);
        }
      });
      toRemove.forEach(o => scene.remove(o));

      if (stateChanged) cbRef.current.syncActiveThreats();
    }

    let frame = 0;
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      frame++;

      if (playingRef.current) {
        if (!spawnedRef.current) spawnSwarm();
        updateThreats();
        if (frame % 15 === 0) cbRef.current.syncActiveThreats();
      }

      scene.children.forEach(c => { if (c.userData.isZone) c.rotation.y += 0.0008; });
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      playingRef.current = false;
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    playingRef.current = !playingRef.current;
    setIsPlaying(playingRef.current);
    if (playingRef.current && logsRef.current.length === 0) {
      pushLog('Scenario 3 started — Multi-domain swarm engagement (10 threats, soft-kill only)', 'info');
    }
  };

  const resetSim = () => {
    playingRef.current = false;
    setIsPlaying(false);
    spawnedRef.current = false;
    logsRef.current = [];
    setLogs([]);
    setActiveThreats([]);
    statsRef.current = { detected: 0, soft_killed: 0, neutralized: 0 };
    setStats({ ...statsRef.current });
    if (sceneRef.current) {
      const toRemove = [];
      sceneRef.current.children.forEach(c => {
        if (c.userData.isEffect) toRemove.push(c);
      });
      threatsRef.current.forEach(t => toRemove.push(t));
      toRemove.forEach(o => sceneRef.current.remove(o));
      threatsRef.current = [];
    }
    pushLog('Scenario 3 reset — Ready', 'info');
  };

  const getStatusColor = (s) => {
    const m = { approaching: 'text-zinc-400', detected: 'text-yellow-500', tracking: 'text-orange-400',
      soft_killed: 'text-green-400', neutralized: 'text-emerald-500' };
    return m[s] || 'text-zinc-400';
  };

  const getLogColor = (t) => {
    const m = { warning: 'text-yellow-400', danger: 'text-red-400', success: 'text-emerald-400' };
    return m[t] || 'text-zinc-400';
  };

  const domainBadge = (d) => {
    const m = {
      air: { c: 'text-sky-400 bg-sky-500/10 border-sky-500/40', label: 'AIR ▲' },
      surface: { c: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40', label: 'SURF ■' },
      underwater: { c: 'text-blue-400 bg-blue-500/10 border-blue-500/40', label: 'SUB ●' },
    };
    return m[d] || { c: '', label: d };
  };

  const totalThreats = SWARM_TEMPLATES.length;
  const allDone = stats.neutralized >= totalThreats;

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="simulation-scenario3">
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <div ref={containerRef} className="w-full h-full" />

          {/* Stats Overlay */}
          <div className="absolute top-4 left-4 flex gap-2" data-testid="sim3-stats">
            {[
              { label: 'Inbound', val: totalThreats, color: 'text-zinc-300 border-zinc-700 bg-zinc-800/70' },
              { label: 'Detected', val: stats.detected, color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
              { label: 'Soft Kill', val: stats.soft_killed, color: 'text-green-400 border-green-500/30 bg-green-500/10' },
              { label: 'Neutralized', val: stats.neutralized, color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
            ].map(st => (
              <div key={st.label} className={`px-3 py-2 rounded border backdrop-blur-md text-center ${st.color}`}>
                <div className="text-lg font-bold font-mono">{st.val}</div>
                <div className="text-[10px] uppercase">{st.label}</div>
              </div>
            ))}
          </div>

          {/* Scenario brief */}
          <div className="absolute top-4 right-4 max-w-xs bg-black/70 backdrop-blur-md border border-red-500/40 p-3 rounded-sm">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Target size={14} className="text-red-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-red-400">Scenario 3 — Swarm Saturation</h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-snug">
              10-threat multi-domain swarm — engage with <span className="text-amber-300">soft-kill only</span> countermeasures.
            </p>
            <div className="mt-2 space-y-0.5 text-[10px] font-mono text-zinc-400">
              <div><span className="text-sky-400">▲ AIR</span> — Jamming · Cyber Takeover · Spoofing</div>
              <div><span className="text-emerald-400">■ SURF</span> — Jamming · Spoofing</div>
              <div><span className="text-blue-400">● SUB</span> — Acoustic Blaster</div>
            </div>
          </div>

          {/* Defense layers legend */}
          <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md border border-zinc-800 p-3 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 text-sky-500 flex items-center gap-1">
              <Shield size={14} /> Defense Layers
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-zinc-400">5km — Detect &amp; Track</span></div>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-zinc-400">2km — Auto Soft Kill</span></div>
            </div>
          </div>

          {/* All clear banner */}
          {allDone && (
            <div className="absolute inset-x-0 top-1/3 flex items-center justify-center pointer-events-none" data-testid="sim3-all-clear">
              <div className="bg-emerald-500/15 backdrop-blur-md border-2 border-emerald-400 rounded-lg px-8 py-4 text-center">
                <div className="text-2xl font-bold uppercase tracking-widest text-emerald-300">All Clear</div>
                <div className="text-xs text-emerald-200 font-mono mt-1">Swarm fully neutralized — {totalThreats}/{totalThreats} soft-kill</div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-zinc-900/50 border-l border-zinc-800 flex flex-col">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <button onClick={togglePlay}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded font-mono text-xs uppercase tracking-wider transition-all ${
                isPlaying ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'bg-green-500/20 text-green-400 border border-green-500/50'
              }`} data-testid="s3-play-btn">
              {isPlaying ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
            </button>
            <button onClick={resetSim}
              className="flex items-center gap-1 px-3 py-2 rounded font-mono text-xs uppercase bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:bg-zinc-700 transition-all"
              data-testid="s3-reset-btn">
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="p-3 border-b border-zinc-800">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Target size={14} className="text-red-500" /> Active Threats
              <span className="ml-auto text-zinc-500 font-mono">{activeThreats.length}/{totalThreats}</span>
            </h3>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {activeThreats.length === 0 ? (
                <p className="text-xs text-zinc-500">{isPlaying ? 'All threats neutralized' : 'Press PLAY to spawn swarm'}</p>
              ) : (
                activeThreats.map(t => {
                  const b = domainBadge(t.domain);
                  return (
                    <div key={t.id} className="flex items-center justify-between text-xs bg-zinc-800/50 rounded px-2 py-1.5" data-testid={`s3-threat-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-white font-mono">{t.id}</span>
                          <span className={`text-[9px] px-1 rounded border ${b.c}`}>{b.label}</span>
                        </div>
                        <div className="text-zinc-500 truncate">{t.name}</div>
                        {t.soft_kill_label && (
                          <div className="text-[10px] text-amber-400 flex items-center gap-1">
                            <Zap size={9} /> {t.soft_kill_label}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-zinc-500 font-mono text-[10px]">{(parseFloat(t.distance) * 0.333).toFixed(1)}km</div>
                        <div className={`font-mono uppercase text-[10px] ${getStatusColor(t.status)}`}>{t.status.replace(/_/g, ' ')}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex-1 p-3 flex flex-col min-h-0">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Radio size={14} /> Event Log
            </h3>
            <div className="flex-1 overflow-y-auto space-y-0.5 font-mono text-[11px] bg-black/30 rounded p-2" data-testid="s3-event-log">
              {logs.length === 0 ? (
                <p className="text-zinc-500">Press PLAY to begin...</p>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className={getLogColor(l.type)}>
                    <span className="text-zinc-600">[{l.ts}]</span> {l.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
